import React, { useEffect, useMemo, useState } from "react";
import {
  Backdrop,
  CircularProgress,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  FormControl,
  FormLabel,
  IconButton,
  Autocomplete,
  Button,
  Box,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DataTable from "comps/Tables/DataTable";
import TLinkApi from "services/tlink.api";
import MDButton from "components/MDButton";
import MRTClientDataTable from "comps/Tables/DataTable/MRTClientDataTable";
import { MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton } from "material-react-table";

const BASE_ENDPOINT = "/configurations/supplier-margin";
const LANG_ENDPOINT = "/masters/language";

const SupplierMarginConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [data, setData] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [supplierId, setSupplierId] = useState("");
  const [languageId, setLanguageId] = useState("");
  const [marginPercUi, setMarginPercUi] = useState("");

  const [editingId, setEditingId] = useState(null);

  const [languages, setLanguages] = useState([]);
  const [languageMap, setLanguageMap] = useState({});

  const getLanguages = async () => {
    try {
      const res = await TLinkApi.get(LANG_ENDPOINT);
      const list = (res?.data ?? [])
        .map((l) => ({ id: Number(l.id), name: String(l.name) }))
        .sort((a, b) => a.name.localeCompare(b.name));
      const withAll = [{ id: -1, name: "All Languages" }, ...list];
      setLanguages(withAll);
      setLanguageMap(Object.fromEntries(withAll.map((l) => [l.id, l.name])));
    } catch (e) {
      console.log(e);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await TLinkApi.get(BASE_ENDPOINT);
      const rows = (res?.data ?? []).map((r) => ({
        id: r.id,
        supplier_id: r.supplier_id,
        supplier_name: r.supplier_name,
        language_id: r.language_id,
        language_name: r.language_name,
        margin_perc: Number(r.margin_perc),
        created_at: r.created_at,
        updated_at: r.updated_at,
      }));
      setData(rows);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getLanguages();
    fetchData();
  }, []);

  const pairToRowId = useMemo(() => {
    const m = new Map();
    data.forEach((r) => m.set(`${r.supplier_id}:${r.language_id}`, r.id ?? null));
    return m;
  }, [data]);

  const openAdd = () => {
    setMode("add");
    setEditingId(null);
    setSupplierId("");
    setLanguageId("");
    setMarginPercUi("");
    setErrorMsg("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setMode("edit");
    setEditingId(row.id ?? null);
    setSupplierId(String(row.supplier_id ?? ""));
    setLanguageId(String(row.language_id ?? ""));

    const pct = Number(row.margin_perc) * 100;
    setMarginPercUi(Number.isFinite(pct) ? String(pct) : "");
    setErrorMsg("");
    setModalOpen(true);
  };

  const valid =
    supplierId.trim() !== "" &&
    languageId.trim() !== "" &&
    marginPercUi.trim() !== "" &&
    !Number.isNaN(Number(marginPercUi));

  const handleSave = async () => {
    if (!valid) {
      setErrorMsg("Please fill all fields correctly.");
      return;
    }
    const sup = String(Number(supplierId));
    const lang = String(Number(languageId));
    const pctNum = Number(marginPercUi);
    if (!Number.isFinite(pctNum) || pctNum < 0 || pctNum > 100) {
      setErrorMsg("Margin (%) must be between 0 and 100.");
      return;
    }

    const key = `${sup}:${lang}`;
    const existingRowId = pairToRowId.get(key);
    if (mode === "add" && existingRowId != null) {
      setErrorMsg("This Supplier & Language already has a margin rule.");
      return;
    }
    if (mode === "edit" && existingRowId != null && existingRowId !== editingId) {
      setErrorMsg("Another record already uses this Supplier & Language.");
      return;
    }

    setSubmitting(true);
    setErrorMsg("");
    try {
      const payload = {
        supplierId: Number(sup),
        languageId: Number(lang),
        marginPerc: pctNum / 100,
      };

      if (mode === "add") {
        await TLinkApi.post(BASE_ENDPOINT, payload);
      } else {
        if (!editingId) {
          setErrorMsg("Missing row id for update.");
          setSubmitting(false);
          return;
        }
        await TLinkApi.update(`${BASE_ENDPOINT}/${editingId}`, payload);
      }

      setModalOpen(false);
      await fetchData();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Something went wrong";
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };
  const oneLine = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

  const columns = useMemo(
    () => [
      {
        accessor: "supplier_id",
        Header: "Supplier Id",
        align: "left",
        Cell: ({ row }) => {
          const v = row?.original?.supplier_id;
          return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>;
        },
      },
      // {
      //   accessor: "supplier_name",
      //   Header: "Supplier Name",
      //   align: "left",
      //   sortable: true,
      //   Cell: ({ row }) => {
      //     const name = row?.original?.supplier_name || "-";
      //     const truncated = name.length > 20 ? name.substring(0, 20) + "..." : name;
      //     return (
      //       <Tooltip title={name.length > 20 ? name : ""}>
      //         <div>{truncated}</div>
      //       </Tooltip>
      //     );
      //   },
      // },
      {
        accessor: "supplier_name",
        Header: "Supplier Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          const name = row?.original?.supplier_name || "";
          const display = name ? (name.length > 20 ? name.slice(0, 20) + "..." : name) : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "language_name",
        Header: "Language",
        align: "left",
        Cell: ({ row }) => {
          const id = Number(row?.original?.language_id);
          const raw = row?.original?.language_name ?? "";
          const name = id === -1 ? "All Languages" : (raw || "-");
          const showTip = id !== -1 && !!raw && raw.length > 20;
          return (
            <Tooltip title={showTip ? raw : ""}>
              <div>{name}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "margin_perc",
        Header: "Margin (%)",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => {
          // const pct = Number(row?.original?.margin_perc) * 100;
          // return `${Number.isFinite(pct) ? pct.toFixed(2) : "0.00"} %`;
          const raw = row?.original?.margin_perc;
          const pct = Number(raw) * 100;
          return Number.isFinite(pct) ? `${pct.toFixed(2)} %` : "-";
        },
      },
      {
        accessor: "actions",
        Header: "Actions",
        align: "right",
        headerAlign: "right",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end" pr={1}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(row.original)} sx={{ p: 0.5 }}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [languageMap]
  );

  const [fullScr, setFullScr] = useState(false);
  const renderToolbarInternalActions = ({ table }) => {
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "0rem" }}>
          <Button variant="contained" color="info" onClick={openAdd}>
            + New
          </Button>
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleFiltersButton table={table} />
          <div onClick={() => setFullScr(!fullScr)} >
            <MRT_ToggleFullScreenButton table={table} />
          </div>
        </div>
      </>
    );
  };

  return (
    <div>
      {isLoading ? (
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : null}

      {/* <DataTable
        table={{ columns, rows: data }}
        isSorted
        entriesPerPage={entriesPerPage}
        canSearch={false}
        tablePage={page}
        setTablePage={setPage}
        setEntriesPerPage={setEntriesPerPage}
        totalLength={data.length}
        pagination
        enableTopToolbar
        tableMaxHeight={data.length === 0 ? "calc(100vh - 220px)" : "calc(100vh - 220px)"}
        tableMinHeight={data.length === 0 ? "calc(100vh - 220px)" : "calc(100vh - 220px)"}
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: window.innerWidth > 1600 ? "calc(76vw - 580px)" : "calc(76vw - 565px)", marginTop: "3px" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Supplier Margin Configuration
            </Typography>
            <Button variant="contained" color="info" onClick={openAdd} sx={{ ml: 2 }}>
              + New
            </Button>
          </Box>
        )}
      /> */}

      <MRTClientDataTable
        table={{ columns, rows: data }}
        isSorted
        canSearch={false}
        enableTopToolbar
        defaultPageSize={entriesPerPage}
        entriesOption={["10", "20", "30", "40", "50"]}
        tableMaxHeight="calc(100vh - 220px)"
        tableMinHeight="calc(100vh - 220px)"
        renderToolbarInternalActions={renderToolbarInternalActions}
        fullScr={fullScr}
        renderTopToolbarCustomActions={() => (
          <Box
          // sx={{
          //   display: "flex",
          //   justifyContent: "space-between",
          //   alignItems: "center",
          //   width:
          //     window.innerWidth > 1600
          //       ? "calc(76vw - 580px)"
          //       : "calc(76vw - 565px)",
          //   mt: "3px",
          // }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Supplier Margin Configuration
            </Typography>
            {/* <Button variant="contained" color="info" onClick={openAdd} sx={{ ml: 2 }}>
              + New
            </Button> */}
          </Box>
        )}
      />

      <Dialog open={modalOpen} onClose={() => !submitting && setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{mode === "add" ? "Add" : "Update"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Supplier ID</FormLabel>
              <TextField
                size="small"
                type="number"
                placeholder="Enter supplier ID"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
              />
            </FormControl>

            <FormControl fullWidth>
              <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Language</FormLabel>
              <Autocomplete
                options={languages}
                value={languageId === "" ? null : (languages.find((l) => l.id === Number(languageId)) || null)}
                onChange={(_, option) => setLanguageId(option ? String(option.id) : "")}
                getOptionLabel={(opt) => opt?.name ?? ""}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder='Select language (or "All Languages")' />
                )}
                disableClearable={false}
              />
            </FormControl>

            <FormControl fullWidth>
              <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Margin (%)</FormLabel>
              <TextField
                size="small"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                placeholder="e.g. 15.5"
                value={marginPercUi}
                onChange={(e) => setMarginPercUi(e.target.value)}
                helperText="Enter percent (0–100). Will be saved as a fraction."
              />
            </FormControl>

            {errorMsg ? (
              <Typography variant="body2" color="error">
                {errorMsg}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setModalOpen(false)} disabled={submitting}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            onClick={handleSave}
            disabled={submitting || !valid}
          >
            {submitting ? (mode === "add" ? "Saving…" : "Updating…") : mode === "add" ? "Save" : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SupplierMarginConfig;